import { useContext, useState } from "react";
import { styled } from "@mui/material/styles";
import { Grid } from "@mui/material";
import ProgramForm from "../components/programForm/programForm";
import SubmitButton from "../components/submitForm/submitForm";
import Typography from "@mui/material/Typography";
import { FormProvider, useForm } from "react-hook-form";
import { Data } from "src/types/index";
import { useEffect } from "react";
import FormService from "src/services/form.service";
import Loader from "src/components/loader/loader";
import { mapDataToPost } from "src/utils/helpers";
import NoData from "src/components/noData/noData";
import { AuthContext } from "src/utils/authContext";
import LogService from "src/services/log.service";
import { ErrorContext } from "src/utils/errorContext";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FormData,
  FormDataContext,
} from "src/context_providers/form_data_context";
import { Button as MuiButton } from "@mui/material";
import { tpDataWithRemarksMock } from "src/mocks/tpDataWithRemarks";
import { ToastContext } from "src/utils/toastContext";

const Div = styled("div")(({ theme }) => ({
  ...theme.typography.h6,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  justifyContent: "center",
  alignItems: "baseline",
  padding: theme.spacing(1),
}));

const MainFormPage = () => {
  const [tpData, setTpData] = useState<Data>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [needRefresh, setNeedRefresh] = useState(true);
  const [formPrefillData, setFormPrefillData] = useState<FormData>();
  const formMethods = useForm();
  const { checkAuthentication, user } = useContext(AuthContext);
  const errorContext = useContext(ErrorContext);
  const toastContext = useContext(ToastContext);
  const navigate = useNavigate(); // Use navigate hook
  useEffect(() => {
    const formRequest = () => {
      const { getData, getDropdownComments } = FormService();
      getDropdownComments()
        .then((data) => {
          setFormPrefillData({
            comments: data.tpComments,
          });
        })
        .catch((error) => {
          setFormPrefillData({
            comments: ["Other"],
          });
          console.error(error);
        });

      getData(user.emailaddress)
        .then((response: Data) => {
          setTpData(response);
          setIsLoading(false);
        })
        .catch((error) => {
          errorContext.setErrorMessage({
            id: Date.now(),
            message: error.message,
          });
          setIsLoading(false);
        });
    };

    if (needRefresh === true) {
      setNeedRefresh(false);
      formRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoading, needRefresh]);

  const onSave = () => {
    try {
      setIsLoading(true);
      const data = mapDataToPost(tpData, formMethods.getValues());
      FormService()
        .saveData(data)
        .then(() => {
          LogService()
            .postLog({
              action_type: "SAVE",
              log_date: new Date().toISOString(),
              tp_email_id: user.emailaddress,
            })
            .catch((error) => {
              errorContext.setErrorMessage({
                id: Date.now(),
                message: error.message,
              });
            });
          toastContext.setToastMessage({
            id: Date.now(),
            message: "Data saved successfully",
          });
        })
        .catch((error) => {
          errorContext.setErrorMessage({
            id: Date.now(),
            message: error.message,
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      errorContext.setErrorMessage({
        id: Date.now(),
        message: "Something went wrong, please try again",
      });
      setIsLoading(false);
    }
  };

  const onSubmit = async () => {
    setIsLoading(true);
    const checkAuth = await checkAuthentication();
    if (!checkAuth) {
      errorContext.setErrorMessage({
        id: Date.now(),
        message: "Your session expired, please log in again",
      });
      setIsLoading(false);
    } else if (checkAuth && tpData) {
      let data = mapDataToPost(tpData, formMethods.getValues());

      FormService()
        .postData(data)
        .then(() => {
          LogService()
            .postLog({
              action_type: "SUBMIT",
              log_date: new Date().toISOString(),
              tp_email_id: user.emailaddress,
            })
            .catch((error) => {
              errorContext.setErrorMessage({
                id: Date.now(),
                message: error.message,
              });
            });
        })
        .catch((error) => {
          errorContext.setErrorMessage({
            id: Date.now(),
            message: error.message,
          });
        })
        .finally(() => {
          setNeedRefresh(true);
        });
    }
  };
  //added for new change
  const goToHistory = () => {
    navigate("/history"); // Navigate to the /history route
  };

  // Prefill the existing response
  useEffect(() => {
    try {
      if (tpData && tpData.tpPrograms && formPrefillData) {
        const existingFormData = Object.fromEntries(
          tpData.tpPrograms.map((program) => [
            program.program_number,
            {
              is_line_listing_correct_and_complete:
                program.isLineListingComplete,
              dropdown_comments:
                program.tpComments &&
                (formPrefillData.comments.includes(program.tpComments)
                  ? program.tpComments
                  : "Other"),
              tp_comments: !formPrefillData.comments.includes(
                program.tpComments,
              )
                ? program.tpComments
                : undefined,
            },
          ]),
        );
        formMethods.reset({
          ...existingFormData,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }, [tpData, formPrefillData]);

  if (isLoading) {
    return <Loader />;
  }

  const isAllFieldsNullExceptLastLogin = (data: Data): boolean => {
    const { lastLogin, ...rest } = data;
    // Check if all values except lastLogin are null
    return Object.values(rest).every((value) => value === null);
  };

  // If tpData is available and all fields except lastLogin are null, render NoData
  if (tpData && isAllFieldsNullExceptLastLogin(tpData)) {
    return <NoData />;
  }

  if (!tpData) {
    return <NoData />;
  }

  return (
    <Grid>
      {/* Display Last Login Date on the right */}
      <Grid container justifyContent="flex-end">
        <Grid item style={{ marginRight: "20px" }}>
          {" "}
          {/* Adjust the marginRight for spacing */}
          <Div>
            Last Login:{" "}
            <Typography variant="body1">{tpData.lastLogin}</Typography>
          </Div>
        </Grid>
      </Grid>
      <Div>{`${user.firstName} ${user.lastname}, Verify the Data Assigned to you for completeness`}</Div>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Div>
            CTV Period Start Date:&nbsp;
            <Typography variant="body1">{tpData.ctv_start_date}</Typography>
          </Div>
        </Grid>
        <Grid item xs={12} sm={6} alignItems="center">
          <Div>
            CTV Period End Date:&nbsp;
            <Typography variant="body1">{tpData.ctv_end_date}</Typography>
          </Div>
        </Grid>
      </Grid>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <FormDataContext.Provider
          value={{ comments: formPrefillData ? formPrefillData.comments : [] }}
        >
          <FormProvider {...formMethods}>
            {tpData.tpPrograms.map((program) => (
              <ProgramForm
                {...formMethods.register(program.program_number)}
                key={program.program_number}
                fieldName={program.program_number}
                data={program.patients}
              />
            ))}
            {/* Container for the buttons */}
            <Grid
              container
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
            >
              {/* History Button (left aligned) */}
              <Grid item>
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={goToHistory}
                  style={{
                    backgroundColor: "rgb(46, 125, 50)",
                    border: "none",
                    borderRadius: "5px",
                    color: "white",
                    fontSize: "12px",
                    padding: "10px 10px",
                    height: "35px",
                  }}
                >
                  HISTORY
                </Button>
              </Grid>
              {/* Submit Button (right aligned) */}
              <Grid item style={{ display: "flex", gap: "10px" }}>
                <MuiButton variant="contained" color="success" onClick={onSave}>
                  Save
                </MuiButton>
                <SubmitButton />
              </Grid>
            </Grid>
          </FormProvider>
        </FormDataContext.Provider>
      </form>
    </Grid>
  );
};

export default MainFormPage;
